import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';

export type ReturnPeriod = 'total' | '1d' | '1w' | '1m' | '1y';

export const RETURN_PERIODS: Array<{ key: ReturnPeriod; label: string; short: string }> = [
  { key: 'total', label: 'Total return vs cost', short: 'Total' },
  { key: '1d', label: 'Daily price change', short: '1D' },
  { key: '1w', label: 'Weekly price change', short: '1W' },
  { key: '1m', label: 'Monthly price change', short: '1M' },
  { key: '1y', label: 'Annual price change', short: '1Y' },
];

export const useReturnPeriod = (storageKey: string) => {
  const [returnPeriod, setReturnPeriod] = useState<ReturnPeriod>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (RETURN_PERIODS.some(p => p.key === stored)) return stored as ReturnPeriod;
    } catch {
      /* ignore */
    }
    return 'total';
  });
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);

  const setPeriod = useCallback((next: ReturnPeriod) => {
    setReturnPeriod(next);
    setPeriodMenuOpen(false);
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  useEffect(() => {
    if (!periodMenuOpen) return;
    const close = () => setPeriodMenuOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [periodMenuOpen]);

  const activePeriod = RETURN_PERIODS.find(p => p.key === returnPeriod) ?? RETURN_PERIODS[0];

  return {
    returnPeriod,
    setPeriod,
    periodMenuOpen,
    setPeriodMenuOpen,
    activePeriod,
  };
};

export const ReturnCell: React.FC<{ percentage: number | null; privacyMode: boolean }> = ({
  percentage,
  privacyMode,
}) => {
  if (privacyMode) {
    return <span className="font-semibold">•••</span>;
  }
  if (percentage == null || !Number.isFinite(percentage)) {
    return <span className="text-slate-500 font-medium">—</span>;
  }
  const isPositive = percentage >= 0;
  return (
    <div className={`flex items-center justify-end gap-1 font-semibold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
      {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {percentage > 0 ? '+' : ''}
      {percentage.toFixed(1)}%
    </div>
  );
};

interface ReturnPeriodHeaderProps {
  darkMode: boolean;
  activeShort: string;
  periodMenuOpen: boolean;
  returnPeriod: ReturnPeriod;
  onToggleMenu: () => void;
  onSelect: (period: ReturnPeriod) => void;
}

export const ReturnPeriodHeader: React.FC<ReturnPeriodHeaderProps> = ({
  darkMode,
  activeShort,
  periodMenuOpen,
  returnPeriod,
  onToggleMenu,
  onSelect,
}) => (
  <th className="text-right pb-2 whitespace-nowrap w-[12%] relative">
    <button
      type="button"
      className={`inline-flex items-center gap-0.5 ml-auto font-inherit hover:opacity-80 transition-opacity ${
        darkMode ? 'text-slate-400' : 'text-slate-600'
      }`}
      title="Click to change return period"
      onClick={e => {
        e.stopPropagation();
        onToggleMenu();
      }}
    >
      {activeShort === 'Total' ? 'Return' : activeShort}
      <ChevronDown size={12} className={`transition-transform ${periodMenuOpen ? 'rotate-180' : ''}`} />
    </button>
    {periodMenuOpen && (
      <div
        className={`absolute right-0 top-full mt-1 z-20 min-w-[9.5rem] rounded-lg border shadow-lg py-1 text-left ${
          darkMode
            ? 'bg-slate-900 border-white/10 text-slate-200'
            : 'bg-white border-slate-200 text-slate-700'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {RETURN_PERIODS.map(period => (
          <button
            key={period.key}
            type="button"
            className={`w-full px-3 py-1.5 text-left text-xs flex items-center justify-between gap-3 ${
              period.key === returnPeriod
                ? darkMode
                  ? 'bg-violet-500/20 text-violet-200'
                  : 'bg-violet-50 text-violet-700'
                : darkMode
                  ? 'hover:bg-white/5'
                  : 'hover:bg-slate-50'
            }`}
            onClick={() => onSelect(period.key)}
          >
            <span className="font-semibold">{period.short}</span>
            <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>{period.label}</span>
          </button>
        ))}
      </div>
    )}
  </th>
);

/** Cost-basis total return % from invested cost and current value. */
export const totalReturnPct = (cost: number, value: number): number | null => {
  if (cost <= 0 || value <= 0 || !Number.isFinite(cost) || !Number.isFinite(value)) return null;
  return ((value - cost) / cost) * 100;
};

export interface PeriodReturnFields {
  dailyReturn?: number | null;
  weeklyReturn?: number | null;
  monthlyReturn?: number | null;
  annualReturn?: number | null;
}

export const pickPeriodReturn = (
  period: ReturnPeriod,
  fields: PeriodReturnFields,
  totalPct: number | null
): number | null => {
  if (period === 'total') return totalPct;
  if (period === '1d') return fields.dailyReturn ?? null;
  if (period === '1w') return fields.weeklyReturn ?? null;
  if (period === '1m') return fields.monthlyReturn ?? null;
  return fields.annualReturn ?? null;
};
